import React, { useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { Button, Dropdown, Groupper, Input, Message, Table } from 'react-frontier';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { useBreadcrumbs } from '../Hooks';
import { UserClass } from '../SiteClasses';
import { TIMEZONE, todayInput, inputToUnix } from '../SiteUtil';
import API from '../API';

ChartJS.register(ArcElement, Tooltip, Legend);

const CELL_WRAP: React.CSSProperties = { whiteSpace: 'normal', wordBreak: 'break-word', minWidth: 140 };

interface ReportRow{ question_id: number, question: string, type_id: number, answer_value: string, total: number }
interface ReportUser{ user_assigned: number | null, user_name: string | null, responses: number, answers: number }
interface ReportPromoter{ question: string, promoter_id: number, promoter_name: string, answer_value: string, total: number }
interface ReportData{ rows: ReportRow[], users: ReportUser[], promoters: ReportPromoter[], responses: number }

function colorAt(i: number){
	return `hsl(${(i * 137.508) % 360}, 62%, 55%)`;
}

function rowStyle(active: boolean): React.CSSProperties{
	return active ? { background: 'var(--color-gray-200, #e6e6e6)', fontWeight: 600 } : undefined;
}

const PercentLabels = {
	id: 'percentLabels',
	afterDatasetsDraw(chart: any){
		var ctx = chart.ctx;
		var values: number[] = chart.data.datasets[0].data || [];
		var total = values.reduce((a, b)=> a + b, 0);
		if(!total) return;
		var meta = chart.getDatasetMeta(0);
		ctx.save();
		ctx.font = 'bold 14px sans-serif';
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';
		ctx.lineWidth = 3;
		ctx.strokeStyle = 'rgba(0,0,0,0.35)';
		ctx.fillStyle = '#fff';
		meta.data.forEach((arc: any, i: number)=>{
			var pct = Math.round((values[i] / total) * 100);
			if(pct < 5) return;
			var pos = arc.tooltipPosition();
			ctx.strokeText(`${pct}%`, pos.x, pos.y);
			ctx.fillText(`${pct}%`, pos.x, pos.y);
		});
		ctx.restore();
	},
};

var SurveyReport = ()=>{
	var { setTitle } = useBreadcrumbs();
	var [dateStart, setDateStart] = useState<string>(DateTime.now().setZone(TIMEZONE).minus({ days: 30 }).toFormat('yyyy-MM-dd'));
	var [dateEnd, setDateEnd] = useState<string>(todayInput());
	var [users, setUsers] = useState<UserClass[]>(null);
	var [userFilter, setUserFilter] = useState<number>(null);
	var [data, setData] = useState<ReportData>(null);
	var [prompts, setPrompts] = useState<string[]>(null);
	var [selected, setSelected] = useState<string>(null);
	var [selectedValue, setSelectedValue] = useState<string>(null);

	useEffect(()=>{
		setTitle('Reporte de encuestas');
		API.getUsers(200, 0).then(res=>{ if(!res.error) setUsers(res.data || []); }).catch(()=>{});
	}, []);

	var generate = (setLoading: (l: boolean)=>void)=>{
		setPrompts(null);
		var start = inputToUnix(dateStart);
		var end = inputToUnix(dateEnd);
		if(start===null || end===null) return setPrompts(['Selecciona fechas válidas.']);
		if(start > end) return setPrompts(['La fecha de inicio no puede ser mayor a la fecha de fin.']);

		setLoading(true);
		API.getSurveysReport(start, end + 86399, userFilter ?? undefined).then(res=>{
			if(res.error) return setPrompts([res.message]);
			setData(res.data);
			setSelected(null);
			setSelectedValue(null);
		}).catch(()=>{
			setPrompts(['Hubo un error generando el reporte. (LCL-RPTS-1)']);
		}).finally(()=>setLoading(false));
	};

	var questions = useMemo(()=>{
		if(!data) return [];
		var out: { question: string, labels: string[], values: number[], total: number }[] = [];
		for(var r of data.rows){
			var q = out.find(o=>o.question===r.question);
			if(!q){
				q = { question: r.question, labels: [], values: [], total: 0 };
				out.push(q);
			}
			q.labels.push(r.answer_value);
			q.values.push(Number(r.total));
			q.total += Number(r.total);
		}
		return out;
	}, [data]);

	var general = useMemo(()=>{
		var labels: string[] = [], values: number[] = [], total = 0;
		for(var q of questions){
			for(var i = 0; i < q.labels.length; i++){
				var li = labels.indexOf(q.labels[i]);
				if(li < 0){ labels.push(q.labels[i]); values.push(0); li = labels.length - 1; }
				values[li] += q.values[i];
				total += q.values[i];
			}
		}
		var order = labels.map((_, i)=>i).sort((a, b)=> labels[a].localeCompare(labels[b], 'es', { numeric: true }));
		return { question: 'General', labels: order.map(i=>labels[i]), values: order.map(i=>values[i]), total };
	}, [questions]);

	var current = selected==null ? general : (questions.find(q=>q.question===selected) || general);
	var selectQuestion = (q: string)=>{ setSelected(q); setSelectedValue(null); };
	var promoterRows = (data?.promoters || []).filter(p=>p.question===selected && (selectedValue==null || p.answer_value===selectedValue));

	return <div style={{ marginTop: 20 }}>
		<Groupper title='Reporte de encuestas' titleCentered actions={
			<Button text='Generar' color='black' onClick={generate} />
		} style={{ maxWidth: 400, margin: 'auto' }}>
			<div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
				<Input label='Fecha inicio' inputType='date' value={dateStart} onChange={setDateStart} style={{ margin: 0, flex: 1 }} />
				<Input label='Fecha fin' inputType='date' value={dateEnd} onChange={setDateEnd} style={{ margin: 0, flex: 1 }} />
			</div>
			<Dropdown
				search
				label='Comercial asignado'
				placeholder='Todos'
				value={userFilter!=null ? String(userFilter) : ''}
				items={[{ text: 'Todos', value: '' }, ...(users || []).map(u=>({ text: `${u.first_name} ${u.last_name}`, value: String(u.user_id) }))]}
				onValueChange={v=>setUserFilter(v!=null && v!=='' ? parseInt(v) : null)}
				style={{ marginTop: 12, marginBottom: 0 }}
			/>
			<Message type='error' list={prompts} style={{ marginTop: 12 }} />
		</Groupper>

		{!!data && (questions.length===0 ? (
			<Groupper title='Resultados' titleCentered style={{ maxWidth: 700, margin: '20px auto 0' }}>
				<div style={{ padding: '30px 20px', textAlign: 'center', color: 'var(--color-gray-400, #999)' }}>
					Sin resultados
				</div>
			</Groupper>
		) : (
			<Groupper title='Resultados' titleCentered style={{ maxWidth: 700, margin: '20px auto 0' }}>
				<div style={{ textAlign: 'center', color: 'var(--color-gray-500, #777)', marginBottom: 12 }}>
					Encuestas contestadas: <b>{data.responses}</b>
				</div>
				<div style={{ overflowX: 'auto' }}>
					<Table fitted striped>
						<Table.Body>
							<Table.Row selectable style={rowStyle(selected==null)} onClick={()=>selectQuestion(null)}>
								<Table.Cell style={CELL_WRAP}><b>General</b></Table.Cell>
								<Table.Cell collapsing centered>{general.total}</Table.Cell>
							</Table.Row>
							{questions.map((q, qi)=>(
								<Table.Row key={`SVRQ-${qi}`} selectable style={rowStyle(selected===q.question)} onClick={()=>selectQuestion(q.question)}>
									<Table.Cell style={CELL_WRAP}>{q.question}</Table.Cell>
									<Table.Cell collapsing centered>{q.total}</Table.Cell>
								</Table.Row>
							))}
						</Table.Body>
					</Table>
				</div>

				<div className='fr centered header' style={{ marginTop: 20, fontSize: 16 }}>{current.question}</div>
				<div style={{ maxWidth: 460, margin: 'auto' }}>
					<Pie
						plugins={[PercentLabels]}
						data={{
							labels: current.labels,
							datasets: [{
								data: current.values,
								backgroundColor: current.labels.map((_, i)=>colorAt(i)),
								borderColor: '#fff',
								borderWidth: 2,
								offset: current.labels.map(l=> l===selectedValue ? 16 : 0),
							}],
						}}
						options={{
							onClick: (_e: any, els: any[])=>{
								if(!els || els.length===0) return;
								var l = current.labels[els[0].index];
								setSelectedValue(v=> v===l ? null : l);
							},
							plugins: {
								legend: {
									position: 'bottom',
									onClick: (_e: any, item: any)=>{
										var l = current.labels[item.index];
										setSelectedValue(v=> v===l ? null : l);
									},
								},
								tooltip: {
									callbacks: {
										label: (ctx: any)=>{
											var val = ctx.parsed;
											var pct = current.total ? Math.round((val / current.total) * 100) : 0;
											return ` ${ctx.label}: ${val} (${pct}%)`;
										},
									},
								},
							},
						}}
					/>
				</div>
				{selected==null && (data.users || []).length > 0 && <>
					<div className='fr centered header' style={{ marginTop: 20, fontSize: 16 }}>Por comercial</div>
					<div style={{ overflowX: 'auto' }}>
						<Table fitted striped>
							<Table.Head>
								<Table.Row>
									<Table.Cell header>Comercial</Table.Cell>
									<Table.Cell header collapsing centered>Encuestas</Table.Cell>
									<Table.Cell header collapsing centered>Respuestas</Table.Cell>
								</Table.Row>
							</Table.Head>
							<Table.Body>
								{data.users.map(u=>(
									<Table.Row key={`SVRU-${u.user_assigned ?? 'none'}`} selectable={u.user_assigned!=null} style={rowStyle(userFilter!=null && userFilter===u.user_assigned)} onClick={u.user_assigned==null ? undefined : ()=>setUserFilter(f=> f===u.user_assigned ? null : u.user_assigned)}>
										<Table.Cell style={CELL_WRAP}>{u.user_name || 'Sin asignar'}</Table.Cell>
										<Table.Cell collapsing centered>{u.responses}</Table.Cell>
										<Table.Cell collapsing centered>{u.answers}</Table.Cell>
									</Table.Row>
								))}
							</Table.Body>
						</Table>
					</div>
					<div style={{ textAlign: 'center', fontSize: 12, color: 'var(--color-gray-400, #999)', marginTop: 6 }}>
						Selecciona un comercial y presiona Generar para filtrar el reporte.
					</div>
				</>}

				<div style={{ margin: '20px -20px -20px', padding: 20, background: '#f5f5f5', overflowX: 'auto' }}>
					<Table fitted striped>
						<Table.Body>
							{current.labels.map((l, i)=>(
								<Table.Row key={`SVRQV-${i}`} selectable style={rowStyle(selectedValue===l)} onClick={()=>setSelectedValue(v=> v===l ? null : l)}>
									<Table.Cell style={CELL_WRAP}>
										<span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 5, background: colorAt(i), marginRight: 8 }} />
										<b>{l}</b>
									</Table.Cell>
									<Table.Cell collapsing centered>{current.values[i]}</Table.Cell>
									<Table.Cell collapsing centered>{current.total ? Math.round((current.values[i] / current.total) * 100) : 0}%</Table.Cell>
								</Table.Row>
							))}
							<Table.Row>
								<Table.Cell style={CELL_WRAP}><b>Total</b></Table.Cell>
								<Table.Cell collapsing centered><b>{current.total}</b></Table.Cell>
								<Table.Cell collapsing centered></Table.Cell>
							</Table.Row>
						</Table.Body>
					</Table>
					{selected!=null && <>
						<div className='fr centered header' style={{ margin: '20px 0 10px', fontSize: 16 }}>Por promotor</div>
						<Table fitted striped>
							<Table.Head>
								<Table.Row>
									<Table.Cell header>Promotor</Table.Cell>
									<Table.Cell header>Respuesta</Table.Cell>
								</Table.Row>
							</Table.Head>
							<Table.Body>
								{promoterRows.map((p, i)=>(
									<Table.Row key={`SVRP-${i}`}>
										<Table.Cell style={CELL_WRAP}>{p.promoter_name}</Table.Cell>
										<Table.Cell style={CELL_WRAP}>{p.answer_value} ({p.total})</Table.Cell>
									</Table.Row>
								))}
							</Table.Body>
						</Table>
					</>}
				</div>
			</Groupper>
		))}
	</div>;
}

export default SurveyReport;
